import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { useAddUserDetailsMutation } from '@ports/internal-services/userDetails/userDetailsSlice';
import { useState, FC } from 'react';
import { setCommonState } from '@ports/common/commonStateSlice';
import { useDispatch } from 'react-redux';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { ConfirmationBox } from '../ConfirmationBox';
import { UploadPhotoModal } from './UploadPhotoModal';

interface UploadOptionsProps {
  showUploadModal: boolean;
  setShowUploadModal: (value: boolean) => void;
  userId: string;
  onUploadSuccess?: () => void; // Add callback prop
}

export const UploadOptions: FC<UploadOptionsProps> = ({
  showUploadModal,
  setShowUploadModal,
  userId,
  onUploadSuccess
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);

  const [UpdateUserDetails] = useAddUserDetailsMutation();

  const handleClose = () => {
    setIsOpenConfirmation(false);
  };

  const removeHandler = () => {
    const dataObj = {
      createUserDetails: {
        profileImage: ''
      }
    };
    UpdateUserDetails(dataObj)
      .then((res: any) => {
        if (res?.data) {
          if (onUploadSuccess) {
            onUploadSuccess();
          }
          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: t('applySuccess')
              },
              scope: 'profile'
            })
          );
          setIsOpenConfirmation(false);
          navigate(routePaths.MyProfile);
        }
      })
      .catch(err => {
        dispatch(
          setAlertDetails({
            alert: {
              type: 'error',
              message: err.message
            },
            scope: 'profile'
          })
        );
      });
  };

  const handleUploadSuccess = async () => {
    // Call the refetch callback after successful upload
    if (onUploadSuccess) {
      await onUploadSuccess();
    }
    setShowUploadModal(false);
  };

  return (
    <>
      <div className="absolute top-[54%] left-[133%] -translate-x-1/2 bg-white shadow-lg rounded-lg py-2 w-[12rem] z-10 border border-gray-200">
        <div
          className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
          onClick={e => {
            e.stopPropagation();
            setShowUploadModal(true);
          }}>
          <SVGAssets.UploadIcon className="w-5 h-5 text-[#7F9D48]" />
          <span className="text-sm font-medium text-[#353535]">
            {t('uploadPhoto')}
          </span>
        </div>
        <div
          className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
          onClick={e => {
            e.stopPropagation();
            dispatch(setCommonState({ isFromPhoto: false }));
            navigate(routePaths.Camera);
          }}>
          <SVGAssets.TakePicIcon className="w-5 h-5 text-[#7F9D48]" />
          <span className="text-sm font-medium text-[#353535]">
            {t('takePicture')}
          </span>
        </div>
        <div
          className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
          onClick={e => {
            e.stopPropagation();
            setIsOpenConfirmation(true);
          }}>
          <SVGAssets.RemoveIcon className="w-5 h-5 text-[#353535]" />
          <span className="text-sm font-medium text-[#353535]">
            {t('removePhoto')}
          </span>
        </div>
      </div>
      {isOpenConfirmation && (
        <ConfirmationBox
          isOpen={isOpenConfirmation}
          onCancel={handleClose}
          onClose={handleClose}
          onConfirm={removeHandler}
          headerTitle=""
          title={t('removePhoto')}
          description={t('removeProfilePhotoConfirmation')}
          icon={<SVGAssets.PaymentFailedIcon className="w-28 h-28" />}
          confirmText={t('continue')}
          cancelText={t('cancel')}
          className="text-center"
        />
      )}
      {showUploadModal && (
        <UploadPhotoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal?.(false)}
          userId={userId}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
    </>
  );
};
